import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Lock, Globe, MoreVertical, X, ChevronDown, ChevronRight, Image as ImageIcon, ImageOff, ImagePlus } from 'lucide-react';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import Header from '../../../shared/components/layout/Header';
import Layout from '../../components/layout/AppLayout';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import {
  getAllBlocksByProject,
  getProjectImages,
  postProjectImagesBulkUpdate,
  uploadFilesToS3,
  postUnitProjectImages
} from '../../services/api/implementation/impl';

const IMAGE_TABS = [
  { id: 'Private', label: 'Private', Icon: Lock },
  { id: 'Public', label: 'Public', Icon: Globe }
];


const layoutCls = (collapsed) =>
  collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]';

const BlockUnitsImages = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { projectsForDropdown, isProjectsLoading } = useProjectsList();
  const { collapsed } = useSidebar();
  const selectedProjectId = projectId ? parseInt(projectId, 10) : null;
  const projectName = projectsForDropdown?.find((p) => p.value == selectedProjectId)?.label ?? (selectedProjectId ? `Project ${selectedProjectId}` : '');
  const [blocks, setBlocks] = useState([]);
  const [images, setImages] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState(IMAGE_TABS[0].id);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedBlocks, setExpandedBlocks] = useState(new Set());
  const menuRef = useRef(null);
  const [imagesRefreshKey, setImagesRefreshKey] = useState(0);
  const [addImagesModalOpen, setAddImagesModalOpen] = useState(false);
  const [addImagesBlockId, setAddImagesBlockId] = useState('');
  const [addImagesFiles, setAddImagesFiles] = useState([]);
  const [addImagesVisibility, setAddImagesVisibility] = useState('private');
  const [isUploadingBlockImages, setIsUploadingBlockImages] = useState(false);
  const addImagesFileInputRef = useRef(null);

  const normalizedImages = useMemo(
    () =>
      images.map((img) => ({
        ...img,
        title: img.title ?? img.name,
        tag: (img.tag ?? img.visibility ?? 'public').toLowerCase()
      })),
    [images]
  );

  const blocksList = useMemo(
    () =>
      blocks.map((block) => {
        const blockImages = normalizedImages.filter(
          (img) =>
            img.block_id === block.id &&
            (img.tag === (activeTab === 'Private' ? 'private' : 'public'))
        );
        return { block, images: blockImages };
      }),
    [blocks, normalizedImages, activeTab]
  );

  const filteredImages = blocksList.flatMap((b) => b.images);
  const filteredIds = filteredImages.map((img) => img.id);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  const toggleBlock = (blockId) => {
    setExpandedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  };

  const expandAllBlocks = () => setExpandedBlocks(new Set(blocks.map((b) => b.id)));
  const collapseAllBlocks = () => setExpandedBlocks(new Set());

  const goToUnitImages = (blockId) => {
    navigate(`/builder/sales/block-units-images/${projectId}/block/${blockId}`);
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => new Set([...prev, ...filteredIds]));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  useEffect(() => {
    setSelectedIds((prev) => new Set([...prev].filter((id) => filteredIds.includes(id))));
  }, [activeTab, filteredIds.length]);

  const setImageTag = async (id, tag) => {
    try {
      await postProjectImagesBulkUpdate({ images: [{ id, visibility: tag }] });
      setImages((prev) =>
        prev.map((img) => (img.id === id ? { ...img, tag, visibility: tag } : img))
      );
      setOpenMenuId(null);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success(`Image set to ${tag}`);
    } catch {
      toast.error('Failed to update image visibility');
    }
  };

  const bulkSetTag = async (tag) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      await postProjectImagesBulkUpdate({
        images: ids.map((id) => ({ id, visibility: tag }))
      });
      setImages((prev) =>
        prev.map((img) => (ids.includes(img.id) ? { ...img, tag, visibility: tag } : img))
      );
      setSelectedIds(new Set());
      toast.success(`${ids.length} image(s) set to ${tag}`);
    } catch {
      toast.error('Failed to update image visibility');
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleAddImagesFileChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
    const valid = files.filter((f) => imageTypes.includes(f.type));
    if (valid.length < files.length) toast.error('Only image files (JPEG, PNG, GIF, WebP, BMP) are allowed.');
    setAddImagesFiles((prev) => [...prev, ...valid]);
    if (addImagesFileInputRef.current) addImagesFileInputRef.current.value = '';
  };

  const removeAddImagesFile = (index) => {
    setAddImagesFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const closeAddImagesModal = () => {
    setAddImagesModalOpen(false);
    setAddImagesBlockId('');
    setAddImagesFiles([]);
    setAddImagesVisibility('private');
    if (addImagesFileInputRef.current) addImagesFileInputRef.current.value = '';
  };

  const handleAddBlockImagesSubmit = async () => {
    if (!addImagesBlockId || addImagesFiles.length === 0 || !selectedProjectId) return;
    const blockIdNum = Number(addImagesBlockId);
    const currentCount = normalizedImages.filter((img) => img.block_id === blockIdNum).length;
    setIsUploadingBlockImages(true);
    try {
      const uploaded = await uploadFilesToS3(addImagesFiles);
      if (uploaded?.length > 0) {
        await postUnitProjectImages({
          images: uploaded.map((f, idx) => ({
            name: f.name,
            url: f.url,
            project_id: Number(selectedProjectId),
            block_id: blockIdNum,
            unit_id: null,
            visibility: addImagesVisibility,
            order: currentCount + idx + 1
          }))
        });
        toast.success(`${uploaded.length} image(s) added to block.`);
        closeAddImagesModal();
        setImagesRefreshKey((k) => k + 1);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to upload images');
    } finally {
      setIsUploadingBlockImages(false);
    }
  };

  useEffect(() => {
    if (!selectedProjectId) {
      setBlocks([]);
      setImages([]);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    const projectIdNum = parseInt(selectedProjectId, 10);

    setBlocksLoading(true);
    setImagesLoading(true);
    setLoadError(null);

    Promise.all([
      getAllBlocksByProject(selectedProjectId),
      getProjectImages(selectedProjectId, 1, 500)
    ])
      .then(([blocksData, imagesRes]) => {
        if (cancelled) return;
        const blocksListRaw = Array.isArray(blocksData) ? blocksData : [];
        setBlocks(blocksListRaw.map((b) => ({ id: b.id, name: b.name ?? `Block ${b.id}` })));

        const list = Array.isArray(imagesRes?.data) ? imagesRes.data : [];
        // Block images: only project_id and block_id (block-level: unit_id null)
        const forProject = list.filter(
          (item) =>
            item.project_id === projectIdNum &&
            item.block_id != null &&
            item.unit_id == null
        );
        setImages(
          forProject.map((item) => ({
            id: item.id,
            name: item.name,
            url: item.url,
            visibility: item.visibility,
            tag: (item.visibility || 'public').toLowerCase(),
            title: item.name,
            block_id: item.block_id,
            unit_id: item.unit_id,
            project_id: item.project_id
          }))
        );
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err?.response?.data?.message || err?.message || 'Failed to load data');
          setBlocks([]);
          setImages([]);
          toast.error('Failed to load blocks and images');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setBlocksLoading(false);
          setImagesLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [selectedProjectId, imagesRefreshKey]);

  // —— No project in URL ——
  if (!projectId) {
    return (
      <>
        <Loader isVisible={isProjectsLoading} message="Loading..." />
        <Layout>
          <div className={`fixed top-0 z-10 transition-all duration-300 ease-in-out bg-white ${layoutCls(collapsed)}`}>
            <Header data={{ name: 'Sales > Block & Units Images' }} />
          </div>
          <div className="mt-[4rem] min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center bg-stone-50/80 px-6">
            <div className="text-center max-w-sm">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-stone-200/80 flex items-center justify-center mb-4">
                <ImageIcon className="w-7 h-7 text-stone-500" />
              </div>
              <h2 className="text-lg font-semibold text-stone-800 mb-1">No project selected</h2>
              <p className="text-sm text-stone-500 mb-4">Go to Images and use &quot;View Block images&quot; for a project to see block & unit images here.</p>
              <button
                type="button"
                onClick={() => navigate('/builder/sales/project-images')}
                className="text-primary font-medium hover:underline"
              >
                Go to Images
              </button>
            </div>
          </div>
        </Layout>
      </>
    );
  }

  // —— Main view with project ——
  return (
    <Layout>
      {/* Header — high z-index so it stays above scrolling content */}
      <div className={`fixed top-0 z-[100] transition-all duration-300 ease-in-out bg-white shadow-sm ${layoutCls(collapsed)}`}>
        <Header data={{ name: 'Sales > Block & Units Images' }} />
      </div>

      {/* Single toolbar: project name, tabs, title, actions — one row */}
      <div className={`fixed top-[4rem] z-[90] bg-white border-b border-stone-100 transition-all duration-300 ${layoutCls(collapsed)}`}>
        <div className="flex flex-nowrap items-center justify-between gap-3 px-6 py-2">
          <div className="flex flex-nowrap items-center gap-3 min-w-0 shrink-0">
            {projectName && (
              <span className="text-sm font-semibold text-stone-800 truncate shrink-0" title={projectName}>
                {projectName}
              </span>
            )}
            <div className="flex rounded-xl bg-stone-100 p-1 shrink-0">
              {IMAGE_TABS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    activeTab === id ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  <Icon size={14} className="shrink-0" />
                  {label}
                </button>
              ))}
            </div>
            <h2 className="text-sm font-semibold text-stone-800 whitespace-nowrap shrink-0">
              {activeTab === 'Private' ? 'Private' : 'Public'} images
            </h2>
          </div>
          <div className="flex flex-nowrap items-center gap-3 text-sm shrink-0">
            {blocksList.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setAddImagesModalOpen(true)}
                  className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 shrink-0"
                >
                  <ImagePlus size={18} />
                  Add block images
                </button>
                <span className="flex items-center gap-2 text-stone-500">
                  <button type="button" onClick={expandAllBlocks} className="font-medium text-stone-600 hover:text-stone-800">
                    Expand all
                  </button>
                  <span className="text-stone-300">|</span>
                  <button type="button" onClick={collapseAllBlocks} className="font-medium text-stone-600 hover:text-stone-800">
                    Collapse all
                  </button>
                </span>
              </>
            )}
            <span className="text-stone-500 tabular-nums">
              {blocksList.length} block{blocksList.length !== 1 ? 's' : ''} · {filteredImages.length} image{filteredImages.length !== 1 ? 's' : ''}
            </span>
            {filteredImages.length > 0 && (
              <label className="flex items-center gap-2 cursor-pointer select-none text-stone-600">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleSelectAll}
                  className="rounded border-stone-300 text-primary focus:ring-primary"
                />
                Select all
              </label>
            )}
          </div>
        </div>

        {/* Bulk actions (in flow of toolbar) */}
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 px-6 py-2 bg-primary/5 border-t border-primary/10">
            <span className="text-sm font-medium text-stone-700">{selectedIds.size} selected</span>
            {activeTab === 'Private' && (
              <button
                type="button"
                onClick={() => bulkSetTag('public')}
                className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 shadow-sm"
              >
                <Globe size={16} />
                Make Public
              </button>
            )}
            {activeTab === 'Public' && (
              <button
                type="button"
                onClick={() => bulkSetTag('private')}
                className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 shadow-sm"
              >
                <Lock size={16} />
                Make Private
              </button>
            )}
            <button
              type="button"
              onClick={clearSelection}
              className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-600 hover:bg-stone-50 ml-auto shadow-sm"
            >
              <X size={16} />
              Clear selection
            </button>
          </div>
        )}
      </div>

      {/* Content — margin increases when bulk actions bar is visible */}
      <div
        className={`overflow-auto bg-stone-50/80 px-6 pb-8 pt-6 ${
          selectedIds.size > 0 ? 'mt-[17rem] min-h-[calc(100vh-17rem)]' : 'mt-[12rem] min-h-[calc(100vh-12rem)]'
        }`}
      >
        {(blocksLoading || imagesLoading) && (
          <div className="flex items-center justify-center py-16">
            <Loader isVisible message="Loading blocks and images..." />
          </div>
        )}
        {!blocksLoading && !imagesLoading && loadError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {loadError}
          </div>
        )}
        {!blocksLoading && !imagesLoading && !loadError && blocks.length === 0 && (
          <div className="flex flex-col flex-1 min-h-[50vh] items-center justify-center rounded-xl border border-stone-200 bg-white py-16 px-6 shadow-sm">
            <ImageOff className="mb-4 h-14 w-14 text-stone-300" strokeWidth={1.25} aria-hidden />
            <p className="text-center text-base font-medium text-stone-600">
              No block images available.
            </p>
            <p className="mt-1 text-center text-sm text-stone-500">
              Add blocks to the project to see block images here.
            </p>
          </div>
        )}
        {!blocksLoading && !imagesLoading && !loadError && blocks.length > 0 && (
        <div className="w-full space-y-4">
          {blocksList.map(({ block, images: blockImages }) => {
            const isExpanded = expandedBlocks.has(block.id);
            return (
              <div
                key={block.id}
                className="rounded-xl border border-stone-200 bg-white shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => toggleBlock(block.id)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-stone-50/80 transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown size={22} className="text-stone-500 shrink-0" />
                  ) : (
                    <ChevronRight size={22} className="text-stone-500 shrink-0" />
                  )}
                  <span className="font-semibold text-stone-800">{block.name}</span>
                  <span className="text-sm text-stone-500">
                    {blockImages.length} image{blockImages.length !== 1 ? 's' : ''}
                  </span>
                </button>

                {isExpanded && (
                  <div className="border-t border-stone-100 bg-stone-50/50 px-5 pb-5 pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                      <span className="text-sm font-medium text-stone-600">Block images</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); goToUnitImages(block.id); }}
                        className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50"
                      >
                        <ImageIcon size={18} />
                        View unit images
                      </button>
                    </div>

                    {blockImages.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-stone-200 bg-white/80 py-12 text-center">
                        <ImageIcon className="mx-auto w-10 h-10 text-stone-300 mb-2" />
                        <p className="text-sm text-stone-500">No {activeTab.toLowerCase()} images in this block</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                        {blockImages.map((img) => (
                          <div
                            key={img.id}
                            className={`relative rounded-xl border-2 bg-white transition-all duration-200 ${
                              selectedIds.has(img.id)
                                ? 'border-primary shadow-md ring-2 ring-primary/20'
                                : 'border-stone-200 hover:border-stone-300 hover:shadow-md'
                            }`}
                          >
                            <div className="aspect-square bg-stone-100 relative overflow-hidden">
                              <img
                                src={img.url}
                                alt={img.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'https://via.placeholder.com/300x300?text=Image';
                                }}
                              />
                            </div>
                            {/* Footer: checkbox, title, menu — stays below image so it never stacks above header when scrolling */}
                            <div className="flex items-center gap-2 p-3 border-t border-stone-100 bg-white relative">
                              <label className="flex items-center shrink-0 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(img.id)}
                                  onChange={() => toggleSelect(img.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="rounded border-stone-300 text-primary focus:ring-primary w-4 h-4"
                                />
                              </label>
                              <p className="text-sm font-medium text-stone-700 truncate min-w-0 flex-1" title={img.title}>
                                {img.title}
                              </p>
                              <div className="relative shrink-0" ref={openMenuId === img.id ? menuRef : null}>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === img.id ? null : img.id); }}
                                  className="p-1.5 rounded-md text-stone-500 hover:bg-stone-100 hover:text-stone-700"
                                  aria-label="Options"
                                >
                                  <MoreVertical size={18} />
                                </button>
                                {openMenuId === img.id && (
                                  <div
                                    className="absolute right-0 top-full mt-1 py-1.5 w-48 bg-white rounded-xl border border-stone-200 shadow-xl z-[80]"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {img.tag === 'private' && (
                                      <button
                                        type="button"
                                        onClick={() => setImageTag(img.id, 'public')}
                                        className="flex items-center gap-2 w-full px-4 py-2.5 text-left text-sm text-stone-700 hover:bg-stone-50"
                                      >
                                        <Globe size={16} />
                                        Make Public
                                      </button>
                                    )}
                                    {img.tag === 'public' && (
                                      <button
                                        type="button"
                                        onClick={() => setImageTag(img.id, 'private')}
                                        className="flex items-center gap-2 w-full px-4 py-2.5 text-left text-sm text-stone-700 hover:bg-stone-50"
                                      >
                                        <Lock size={16} />
                                        Make Private
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>

      {addImagesModalOpen && (
        <Modal
          isDirty={addImagesFiles.length > 0 || !!addImagesBlockId}
          confirmOnClose={true}
          name="Add block images"
          width={45}
          onClose={closeAddImagesModal}
        >
          <div className="flex flex-col gap-4 p-2">
            <div>
              <label className="mb-1 block font-medium text-stone-700">Block</label>
              <select
                value={addImagesBlockId}
                onChange={(e) => setAddImagesBlockId(e.target.value)}
                className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm text-stone-800 focus:border-primary focus:ring-1 focus:ring-primary"
              >
                <option value="">Select block</option>
                {blocks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block font-medium text-stone-700">Visibility</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="addImagesVisibility"
                    checked={addImagesVisibility === 'private'}
                    onChange={() => setAddImagesVisibility('private')}
                    className="rounded border-stone-300 text-primary focus:ring-primary"
                  />
                  <Lock size={16} className="text-stone-500" />
                  Private
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="addImagesVisibility"
                    checked={addImagesVisibility === 'public'}
                    onChange={() => setAddImagesVisibility('public')}
                    className="rounded border-stone-300 text-primary focus:ring-primary"
                  />
                  <Globe size={16} className="text-stone-500" />
                  Public
                </label>
              </div>
            </div>
            <div>
              <label className="mb-1 block font-medium text-stone-700">Images</label>
              <input
                ref={addImagesFileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
                multiple
                className="hidden"
                onChange={handleAddImagesFileChange}
              />
              <button
                type="button"
                onClick={() => addImagesFileInputRef.current?.click()}
                className="flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
              >
                <ImagePlus size={18} />
                Select images
              </button>
              {addImagesFiles.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {addImagesFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-2 py-1.5 text-sm"
                    >
                      <span className="truncate max-w-[180px]" title={file.name}>{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeAddImagesFile(index)}
                        className="p-0.5 rounded hover:bg-stone-200 text-stone-500"
                        aria-label="Remove"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
              <button
                type="button"
                onClick={closeAddImagesModal}
                className="rounded-lg border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddBlockImagesSubmit}
                disabled={!addImagesBlockId || addImagesFiles.length === 0 || isUploadingBlockImages}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              >
                {isUploadingBlockImages ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default BlockUnitsImages;
